import type { SceneNode, SceneDocument } from '@/lib/scene-document';

import { useStore } from 'zustand';
import { useTranslation } from 'react-i18next';
import { getRichList, getRichSpans, collectSceneFontVariants } from '@nexmail/shared-util';
import { ensureFontVariantsLoaded } from '@/lib/canvaskit-init';
/**
 * Interactive CanvasKit (Skia WASM) editor component.
 * Thin shell that composes hooks for surface, rendering, and events.
 * All state lives in a Zustand store provided via context.
 */
import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useContext,
  useCallback,
  createContext,
  useImperativeHandle,
} from 'react';

import LinkIcon from '@mui/icons-material/Link';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { Box, Paper, Tooltip, Popover, Divider, IconButton, Typography } from '@mui/material';

import type { ProductVariantSearchResultDto } from '@/apis/backend/generated/models';
import {
  pickedProductFromDto,
  findProductBlockRoot,
  buildProductApplyPatches,
} from './product-binding-web';
import { useEditorEvents } from './useEditorEvents';
import { SelectionToolbar } from './SelectionToolbar';
import { CanvasShortcutsHelp } from './CanvasShortcutsHelp';
import { EmojiPickerButton } from '../emoji/EmojiPicker';
import { useEditorRenderer } from './useEditorRenderer';
import { useCanvasKitSurface } from './useCanvasKitSurface';
import { applyImageToTargetOrPlace } from './canvas-place-asset';
import { extractRichText, spansToOverlayHtml, sanitizePastedHtml } from './canvaskit-rich-text';
import { generateNodeId, updateNode as mutUpdateNode } from './canvaskit-mutations';
import { getNodeAABB, getNodeWorldBounds, getParentWorldOffset } from './canvaskit-hit-test';
import {
  renderNode,
  getCachedImage,
  applyFillToPaint,
  type RenderContext,
} from './canvaskit-render-scene';
import { resolveDocColorsMemo, resolveColorTokenForDisplay } from './canvas-brand-resolve';
import type { BrandResolverContext } from '@nexmail/shared-util/email-builder';
import {
  type CanvasEditorStore,
  type CanvasEditorState,
  createCanvasEditorStore,
  type CanvasEditorActions,
  textAutoResizeDimPatch,
} from './canvaskit-editor-store';
import {
  isOsFileDrag,
  readOsImageFiles,
  isCanvasAssetDrag,
  type DropPlacement,
  resolveDropPlacement,
  handleCanvasAssetDrop,
} from './canvas-dnd';

import type { DrawToolType } from './canvaskit-editor-store';

// ─── Store context ────────────────────────────────────────────────────────

const EditorStoreContext = createContext<CanvasEditorStore | null>(null);

export function useEditorStore<T>(
  selector: (state: CanvasEditorState & CanvasEditorActions) => T
): T {
  const store = useContext(EditorStoreContext);
  if (!store) throw new Error('useEditorStore must be used within CanvasKitEditor');
  return useStore(store, selector);
}

/** Returns true if an EditorStoreContext is available (safe to use useEditorStore). */
export function useHasEditorStore(): boolean {
  return useContext(EditorStoreContext) !== null;
}

export { EditorStoreContext };

/**
 * Standalone provider that creates a store and makes it available to children.
 * Use when the properties panel / layers panel need to live outside the editor component.
 */
export function CanvasKitEditorProvider({
  initialDocument,
  artboardFormat,
  brandContext,
  children,
}: {
  initialDocument: SceneDocument;
  /** 'email' upgrades legacy docs to a hugging (auto-height) email artboard on load. */
  artboardFormat?: 'email';
  /** Brand resolver context used to resolve `$brand.color.*` tokens for display. */
  brandContext?: BrandResolverContext | null;
  children: React.ReactNode;
}) {
  const storeRef = useRef<CanvasEditorStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createCanvasEditorStore(initialDocument, { artboardFormat, brandContext });
  }
  // Keep the store's brand context in sync when the host's brand changes (brand
  // swap re-themes token colors). No-ops when the reference is unchanged.
  useEffect(() => {
    storeRef.current?.getState().setBrandContext(brandContext ?? null);
  }, [brandContext]);
  return (
    <EditorStoreContext.Provider value={storeRef.current}>{children}</EditorStoreContext.Provider>
  );
}

// ─── Handle type ──────────────────────────────────────────────────────────

export interface CanvasKitEditorHandle {
  getDocument(): SceneDocument;
  getStore(): CanvasEditorStore;
  toDataURL(opts?: { mimeType?: string; pixelRatio?: number }): string | null;
  toSelectionDataURL(opts?: {
    mimeType?: string;
    pixelRatio?: number;
    padding?: number;
  }): string | null;
  /**
   * Build a normalized standalone SceneDocument from the current selection
   * (selection bbox → origin, doc sized to the bbox). Returns null when nothing
   * (other than the background) is selected. Used to export the selection as HTML.
   */
  toSelectionDoc(): SceneDocument | null;

  // Node creation
  addRect(): void;
  addEllipse(): void;
  addText(text?: string): void;
  addTextbox(text: string, opts?: { textAlign?: 'left' | 'center' | 'right' | 'justify' }): void;
  addImage(url: string): void;
  /** Place a PRODUCT on the canvas with full data binding. When the current
   *  selection sits inside an existing product block, the whole block is
   *  rebound to this product (image + title + price + links together);
   *  otherwise a bound, click-through product image is placed at center. */
  addProductImage(product: ProductVariantSearchResultDto): void;
  /** Place an image with an explicit drop placement: fills the target shape
   *  (or replaces the target image) when given, else places a photo node at
   *  the scene point. Used by OS file drops after upload. */
  placeImage(
    url: string,
    placement?: { targetNodeId?: string; atScene?: { x: number; y: number } }
  ): void;
  addLine(): void;
  addStar(): void;
  addPolygon(): void;
  addTrapezoid(): void;
  addSeal(opts?: { sides?: number; innerRadius?: number; bumpStyle?: 'sharp' | 'wavy' }): void;
  addButton(): void;
  addBadge(shape: 'circle' | 'rounded-rect' | 'starburst' | 'ribbon' | 'tag', text?: string): void;
  setDrawTool(tool: DrawToolType | null): void;

  // Mutations
  deleteSelected(): void;
  undo(): void;
  redo(): void;
  canUndo: boolean;
  canRedo: boolean;
  bringForward(): void;
  sendBackward(): void;
  bringToFront(): void;
  sendToBack(): void;
  groupSelected(): void;
  ungroupSelected(): void;

  // Vector operations
  textToOutlines(nodeId: string): void;
  booleanOp(op: 'union' | 'subtract' | 'intersect' | 'xor'): void;
  importSvg(svgString: string): void;

  // Link zones
  addLinkZone(): void;
  setLinkZoneLayout(layoutId: string): void;

  // Document-level
  loadDocument(doc: SceneDocument): void;
  addTemplate(doc: SceneDocument, name: string): void;
  resizeCanvas(width: number, height: number): void;
  resetHistory(): void;
  toJSON(): SceneDocument;
}

// ─── Props ────────────────────────────────────────────────────────────────

export interface CanvasContextMenuEvent {
  mouseX: number;
  mouseY: number;
  selectedIds: string[];
  hasGroup: boolean;
  canUngroup: boolean;
}

interface CanvasKitEditorProps {
  initialDocument: SceneDocument;
  onDocumentChange?: (doc: SceneDocument) => void;
  onSelectionChange?: (nodeId: string | null, node: SceneNode | null) => void;
  onZoomChange?: (zoom: number) => void;
  onContextMenu?: (event: CanvasContextMenuEvent) => void;
  children?: React.ReactNode;
  /** When true, skips the fit-to-viewport zoom on mount. Use for inline/embedded contexts where 1:1 scale is required. */
  disableAutoFit?: boolean;
  /** When true, scroll-wheel zoom/pan are disabled. Use for canvas sections embedded inside EmailCanvasViewport. */
  lockViewport?: boolean;
  /** 'email' upgrades legacy docs to a hugging (auto-height) email artboard on load. Only used when the editor owns its store (no surrounding provider). */
  artboardFormat?: 'email';
  /** Brand resolver context for `$brand.color.*` token display. Only used when
   *  the editor owns its store; with a surrounding CanvasKitEditorProvider the
   *  provider owns the brand context. */
  brandContext?: BrandResolverContext | null;
  /** Initial viewport fit. 'contain' (default) fits the whole doc; 'fit-width-top' fits the doc width and aligns the top edge — better for tall email pages. */
  initialFit?: 'contain' | 'fit-width-top';
  /** Fonts CSS / Google Fonts URL for the document's fonts, so text renders in
   * the design's actual font (e.g. the imported Figma font) instead of falling
   * back to bundled Roboto. */
  fontsImportUrl?: string;
  /**
   * Called when the user drops OS image files (desktop drag) onto the canvas.
   * The owner uploads them and places the resulting URLs (e.g. via the
   * `placeImage` handle method with the given placement). Without this prop,
   * OS file drops are ignored.
   */
  onOsFilesDropped?: (files: File[], placement: DropPlacement) => void;
}

// ─── Inline text editing overlay ──────────────────────────────────────────

function InlineTextOverlay({ store }: { store: CanvasEditorStore }) {
  const editingTextId = useStore(store, (s) => s.editingTextId);
  const doc = useStore(store, (s) => s.doc);
  const zoom = useStore(store, (s) => s.zoom);
  const panOffset = useStore(store, (s) => s.panOffset);
  const brandContext = useStore(store, (s) => s.brandContext);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [effectiveFontSize, setEffectiveFontSize] = useState<number | null>(null);
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  // When a format action steals focus (window.prompt for a link), the
  // contentEditable blurs — suppress the one commit that blur would trigger so
  // we stay in edit mode and can re-apply to the saved selection.
  const suppressCommitRef = useRef(false);
  const savedRangeRef = useRef<Range | null>(null);

  // Set initial text, auto-shrink font to match renderer, and select all
  useEffect(() => {
    if (!editingTextId || !overlayRef.current) return;
    const el = overlayRef.current;
    const node = doc.nodes[editingTextId];
    if (!node) return;
    const initialText = node.text ?? '';
    // Seed styled HTML so existing rich-text formatting (+ lists) shows while editing.
    el.innerHTML = spansToOverlayHtml(
      initialText,
      getRichSpans(node.pluginData, initialText.length),
      getRichList(node.pluginData),
      { fontSize: node.fontSize, fontFamily: node.fontFamily }
    );
    // Prefer inline CSS (font-weight/color) over <b>/<font> so the DOM->spans
    // extractor reads styles uniformly off ancestor inline styles.
    try {
      document.execCommand('styleWithCSS', false, 'true');
    } catch {
      /* older browsers */
    }

    // Auto-shrink: mirror the CanvasKit renderer's font shrink logic.
    // Temporarily measure at base font size and shrink if text overflows node height.
    const baseFontSize = (node.fontSize ?? 16) * zoom;
    const MIN_FONT_SIZE = 8 * zoom;
    const nodeScreenHeight = node.height * zoom;
    // AUTO_HEIGHT/AUTO_WIDTH boxes grow to fit at the natural font size (the
    // renderer skips shrink-to-fit for them), so the overlay must not shrink
    // either — otherwise the caret font wouldn't match the committed pixels.
    const autoResizesToContent =
      node.textAutoResize === 'AUTO_HEIGHT' || node.textAutoResize === 'AUTO_WIDTH';

    if (
      !autoResizesToContent &&
      nodeScreenHeight > 0 &&
      el.scrollHeight > nodeScreenHeight &&
      baseFontSize > MIN_FONT_SIZE
    ) {
      let fs = baseFontSize - 2 * zoom;
      while (fs > MIN_FONT_SIZE) {
        el.style.fontSize = `${fs}px`;
        if (node.letterSpacing)
          el.style.letterSpacing = `${node.letterSpacing * (fs / (node.fontSize ?? 16))}px`;
        if (el.scrollHeight <= nodeScreenHeight) break;
        fs -= 2 * zoom;
      }
      // Refine: try 1px larger to find biggest size that fits
      if (el.scrollHeight <= nodeScreenHeight && fs + zoom < baseFontSize) {
        el.style.fontSize = `${fs + zoom}px`;
        if (el.scrollHeight > nodeScreenHeight) {
          el.style.fontSize = `${fs}px`;
        } else {
          fs = fs + zoom;
        }
      }
      setEffectiveFontSize(fs);
    } else {
      setEffectiveFontSize(baseFontSize);
    }

    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editingTextId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-resize the node as the user types so selection handles follow. The box
  // grows/shrinks per the node's textAutoResize intent (height for AUTO_HEIGHT,
  // both dims for AUTO_WIDTH, grow-only-height when the field is absent). The
  // editable is laid out unwrapped for AUTO_WIDTH so scrollWidth reports the
  // longest line rather than the (clamped) container width.
  const syncSize = useCallback(() => {
    if (!overlayRef.current) return;
    const el = overlayRef.current;
    const contentHeight = el.scrollHeight / zoom;
    const contentWidth = el.scrollWidth / zoom;
    const state = store.getState();
    const nodeId = state.editingTextId;
    if (!nodeId) return;
    const node = state.doc.nodes[nodeId];
    if (!node) return;
    const patch = textAutoResizeDimPatch(node.textAutoResize, node, contentWidth, contentHeight);
    if (Object.keys(patch).length === 0) return;
    const nextDoc = mutUpdateNode(state.doc, nodeId, patch);
    store.setState({ doc: nextDoc, renderVersion: state.renderVersion + 1 });
  }, [store, zoom]);

  const handleBlur = useCallback(() => {
    if (suppressCommitRef.current) {
      suppressCommitRef.current = false;
      return;
    }
    if (!overlayRef.current) return;
    const state = store.getState();
    const editNode = state.editingTextId ? state.doc.nodes[state.editingTextId] : undefined;
    const { text, spans, list } = extractRichText(overlayRef.current, {
      fontSize: editNode?.fontSize,
      fontFamily: editNode?.fontFamily,
    });
    const contentHeight = overlayRef.current.scrollHeight / zoom;
    const contentWidth = overlayRef.current.scrollWidth / zoom;
    store.getState().commitTextEdit(text, contentHeight, spans, list, contentWidth);
  }, [store, zoom]);

  // Apply an execCommand-based format to the current selection without losing
  // focus (buttons preventDefault on mousedown). Re-sync height afterwards since
  // bolding can re-wrap. styleWithCSS keeps the markup as inline styles.
  const applyFormat = useCallback(
    (command: string, value?: string) => {
      const el = overlayRef.current;
      if (!el) return;
      el.focus();
      try {
        document.execCommand('styleWithCSS', false, 'true');
      } catch {
        /* noop */
      }
      document.execCommand(command, false, value);
      syncSize();
    },
    [syncSize]
  );

  // Insert an emoji at the caret. The overlay is a live contentEditable in the
  // main document, so this mirrors applyFormat: focus, execCommand('insertText'),
  // re-sync the box. extractRichText picks the glyph up on the next blur/commit,
  // and the CanvasKit raster path renders it via the Noto Color Emoji fallback.
  const insertEmoji = useCallback(
    (emoji: string) => {
      const el = overlayRef.current;
      if (!el) return;
      el.focus();
      document.execCommand('insertText', false, emoji);
      syncSize();
    },
    [syncSize]
  );

  // Link: window.prompt blurs the editable, so guard the commit, then restore
  // the saved selection before createLink/unlink.
  const applyLink = useCallback(() => {
    const el = overlayRef.current;
    if (!el) return;
    const sel = window.getSelection();
    savedRangeRef.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    suppressCommitRef.current = true;
    const url = window.prompt('Link URL', 'https://');
    el.focus();
    const restore = window.getSelection();
    if (savedRangeRef.current && restore) {
      restore.removeAllRanges();
      restore.addRange(savedRangeRef.current);
    }
    if (url === null) return; // cancelled
    if (url.trim() === '') applyFormat('unlink');
    else applyFormat('createLink', url.trim());
  }, [applyFormat]);

  // Insert clipboard HTML as the overlay's own markup instead of letting the
  // browser splice the source's tree in verbatim. Plain-text pastes carry no
  // markup to normalize, so they fall through to the default handler.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const html = e.clipboardData.getData('text/html');
      if (!html.trim()) return;
      e.preventDefault();
      const state = store.getState();
      const editNode = state.editingTextId ? state.doc.nodes[state.editingTextId] : undefined;
      const clean = sanitizePastedHtml(html, {
        fontSize: editNode?.fontSize,
        fontFamily: editNode?.fontFamily,
      });
      if (clean) document.execCommand('insertHTML', false, clean);
      else document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
      syncSize();
    },
    [store, syncSize]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        store.getState().cancelTextEdit();
      }
      // Prevent editor keyboard shortcuts while typing
      e.stopPropagation();
    },
    [store]
  );

  const handleInput = useCallback(() => {
    syncSize();
  }, [syncSize]);

  // Reset effective font size when leaving edit mode
  useEffect(() => {
    if (!editingTextId) setEffectiveFontSize(null);
  }, [editingTextId]);

  if (!editingTextId) return null;
  const node = doc.nodes[editingTextId];
  if (!node || node.type !== 'TEXT') return null;

  const bounds = getNodeWorldBounds(doc, editingTextId);

  // Convert scene coordinates to screen coordinates
  const left = bounds.x * zoom + panOffset.x;
  const top = bounds.y * zoom + panOffset.y;
  const width = bounds.width * zoom;
  const height = bounds.height * zoom;

  const fill = node.fills?.[0];
  // Resolve a brand token to a real color so the contentEditable caret/text
  // matches the canvas while editing (a raw token is invalid CSS → black).
  const color =
    fill && fill.type === 'SOLID'
      ? resolveColorTokenForDisplay(fill.color ?? '#000000', brandContext)
      : '#000000';

  const baseFontSize = (node.fontSize ?? 16) * zoom;
  const fontSize = effectiveFontSize ?? baseFontSize;
  const lh = node.lineHeight ?? 1.2;

  // Vertical alignment matches the canvas (which offsets the whole paragraph by
  // its getHeight()) and the scene-to-html exporter: a flex column whose
  // justify-content positions the full text block. Hand-computing a padding
  // from a single line's height pushed multi-line text down by ~1.5 lines.
  const verticalJustify =
    node.verticalAlign === 'middle'
      ? 'center'
      : node.verticalAlign === 'bottom'
        ? 'flex-end'
        : 'flex-start';

  // AUTO_WIDTH: lay the editable out unwrapped and sized to its content so
  // scrollWidth reports the longest line (and the box can shrink on delete).
  const noWrap = node.textAutoResize === 'AUTO_WIDTH';

  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: verticalJustify,
        transform: bounds.rotation ? `rotate(${bounds.rotation}deg)` : undefined,
        transformOrigin: 'top left',
        zIndex: 10,
        pointerEvents: 'none',
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Paper
        elevation={4}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          left: 0,
          top: -44,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.5,
          py: 0.25,
          borderRadius: 2,
          zIndex: 11,
          pointerEvents: 'auto',
        }}
      >
        {(
          [
            ['bold', 'Bold', FormatBoldIcon],
            ['italic', 'Italic', FormatItalicIcon],
            ['underline', 'Underline', FormatUnderlinedIcon],
            ['strikeThrough', 'Strikethrough', StrikethroughSIcon],
          ] as [string, string, typeof FormatBoldIcon][]
        ).map(([cmd, label, Icon]) => (
          <Tooltip key={cmd} title={label}>
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormat(cmd)}
            >
              <Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        ))}
        <Tooltip title="Link">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={applyLink}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Text color">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => setColorAnchor(e.currentTarget)}
          >
            <FormatColorTextIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Tooltip title="Bulleted list">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('insertUnorderedList')}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered list">
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyFormat('insertOrderedList')}
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <EmojiPickerButton onPick={insertEmoji} />
      </Paper>

      <Popover
        open={!!colorAnchor}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75, p: 1 }}>
          {[
            '#111827',
            '#FFFFFF',
            '#EF4444',
            '#F59E0B',
            '#10B981',
            '#3B82F6',
            '#6366F1',
            '#EC4899',
          ].map((c) => (
            <Box
              key={c}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                applyFormat('foreColor', c);
                setColorAnchor(null);
              }}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: c,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { transform: 'scale(1.12)' },
                transition: 'transform 0.08s ease-out',
              }}
            />
          ))}
        </Box>
      </Popover>

      <div
        ref={overlayRef}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onPaste={handlePaste}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          width: noWrap ? 'max-content' : '100%',
          alignSelf: noWrap ? 'flex-start' : undefined,
          fontSize,
          // Match the canvas: use the node's real face (loaded into the DOM via
          // canvaskit-init's mirrored <style>), falling back to sans-serif —
          // never the browser-default serif — during the async font load.
          fontFamily: node.fontFamily ? `${node.fontFamily}, sans-serif` : 'Roboto, sans-serif',
          fontWeight: node.fontWeight ?? 400,
          fontStyle: node.fontStyle ?? 'normal',
          textAlign: (node.textAlign as React.CSSProperties['textAlign']) ?? 'left',
          lineHeight: `${lh}`,
          letterSpacing: node.letterSpacing ? `${node.letterSpacing * zoom}px` : undefined,
          textDecoration: node.textDecoration === 'none' ? undefined : node.textDecoration,
          textTransform:
            node.textTransform === 'none'
              ? undefined
              : (node.textTransform as React.CSSProperties['textTransform']),
          color,
          caretColor: color,
          outline: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          border: 'none',
          boxSizing: 'border-box',
          cursor: 'text',
          whiteSpace: noWrap ? 'pre' : 'pre-wrap',
          wordBreak: noWrap ? 'normal' : 'break-word',
          pointerEvents: 'auto',
        }}
      />
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────

export const CanvasKitEditor = forwardRef<CanvasKitEditorHandle, CanvasKitEditorProps>(
  function CanvasKitEditor(
    {
      initialDocument,
      onDocumentChange,
      onSelectionChange,
      onZoomChange,
      onContextMenu: onContextMenuProp,
      children,
      disableAutoFit,
      lockViewport,
      artboardFormat,
      brandContext,
      initialFit,
      fontsImportUrl,
      onOsFilesDropped,
    },
    ref
  ) {
    const { t } = useTranslation('canvas');
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // ── Store: use existing context if available, else create ─────────
    const existingStore = useContext(EditorStoreContext);
    const ownStoreRef = useRef<CanvasEditorStore | null>(null);
    if (!existingStore && !ownStoreRef.current) {
      ownStoreRef.current = createCanvasEditorStore(initialDocument, { artboardFormat, brandContext });
    }
    const store = existingStore ?? ownStoreRef.current!;

    // Sync brand context into the owned store on change (no-op with a provider,
    // which owns the brand context itself). Keeps token colors re-themable.
    useEffect(() => {
      if (existingStore) return;
      ownStoreRef.current?.getState().setBrandContext(brandContext ?? null);
    }, [existingStore, brandContext]);

    // ── Hooks ────────────────────────────────────────────────────────
    const { ready, error, containerSize, getRefs, surfaceRef, fontEpoch } = useCanvasKitSurface(
      containerRef,
      canvasRef,
      fontsImportUrl
    );
    useEditorRenderer(store, getRefs, ready, fontEpoch);
    const events = useEditorEvents(store, canvasRef, { lockViewport, onOsFilesDropped });
    const didInitialFitRef = useRef(false);

    // When locked, keep panOffset pinned to (0,0) so any accidental drift is corrected
    useEffect(() => {
      if (!lockViewport) return;
      return store.subscribe((s) => {
        if (s.panOffset.x !== 0 || s.panOffset.y !== 0) {
          store.setState({ panOffset: { x: 0, y: 0 }, renderVersion: s.renderVersion + 1 });
        }
      });
    }, [store, lockViewport]);

    // Fit canvas to viewport on initial load
    useEffect(() => {
      if (ready && surfaceRef.current && containerSize.w > 0 && containerSize.h > 0) {
        const s = store.getState();
        const docW = s.doc.width;
        const docH = s.doc.height;

        if (disableAutoFit || lockViewport) {
          // Embedded/locked mode: set 1:1 once on mount, then leave state alone
          if (!didInitialFitRef.current) {
            didInitialFitRef.current = true;
            store.setState({
              zoom: 1,
              panOffset: { x: 0, y: 0 },
              renderVersion: s.renderVersion + 1,
            });
          } else {
            // The surface was recreated because the container resized (e.g.
            // the canvas section's height was dragged). useCanvasKitSurface
            // builds a fresh blank GL surface; without a renderVersion bump
            // the renderer doesn't know to repaint to it and the new surface
            // stays blank until some unrelated state change wakes it up.
            store.setState({ renderVersion: s.renderVersion + 1 });
          }
          return;
        }

        if (!didInitialFitRef.current) {
          // First time: compute fit-to-viewport zoom
          didInitialFitRef.current = true;
          const padding = 40;
          const scaleX = (containerSize.w - padding * 2) / docW;
          if (initialFit === 'fit-width-top') {
            // Tall pages (emails): fit the width, align the top edge.
            const zoom = Math.min(scaleX, 1);
            store.setState({
              zoom,
              panOffset: { x: (containerSize.w - docW * zoom) / 2, y: padding },
              renderVersion: s.renderVersion + 1,
            });
          } else {
            const scaleY = (containerSize.h - padding * 2) / docH;
            const zoom = Math.min(scaleX, scaleY, 1);
            const panX = (containerSize.w - docW * zoom) / 2;
            const panY = (containerSize.h - docH * zoom) / 2;

            store.setState({
              zoom,
              panOffset: { x: panX, y: panY },
              renderVersion: s.renderVersion + 1,
            });
          }
        } else {
          // Subsequent resizes (e.g. sidebar toggle): keep zoom, re-center
          // horizontally; keep the user's vertical position in fit-width mode.
          const zoom = s.zoom;
          const panX = (containerSize.w - docW * zoom) / 2;
          const panY =
            initialFit === 'fit-width-top' ? s.panOffset.y : (containerSize.h - docH * zoom) / 2;

          store.setState({
            panOffset: { x: panX, y: panY },
            renderVersion: s.renderVersion + 1,
          });
        }
      }
    }, [ready, store, surfaceRef, containerSize.w, containerSize.h, disableAutoFit, initialFit]);

    // ── External change callbacks ────────────────────────────────────
    const onDocRef = useRef(onDocumentChange);
    onDocRef.current = onDocumentChange;
    const onSelRef = useRef(onSelectionChange);
    onSelRef.current = onSelectionChange;
    const onZoomRef = useRef(onZoomChange);
    onZoomRef.current = onZoomChange;

    useEffect(() => {
      // On-demand font loading: when the live scene starts using a variant that
      // isn't loaded yet (a freshly toggled italic, a newly picked weight),
      // fetch the real face and redraw. Debounced so typing/rapid edits coalesce;
      // ensureFontVariantsLoaded no-ops unless a new variant actually appears.
      let fontSyncTimer: ReturnType<typeof setTimeout> | null = null;
      const scheduleFontSync = (doc: SceneDocument) => {
        if (fontSyncTimer) clearTimeout(fontSyncTimer);
        fontSyncTimer = setTimeout(() => {
          ensureFontVariantsLoaded(collectSceneFontVariants(doc.nodes));
        }, 200);
      };
      const unsub = store.subscribe((state, prev) => {
        if (state.doc !== prev.doc) {
          onDocRef.current?.(state.doc);
          scheduleFontSync(state.doc);
        }
        if (state.selectedIds !== prev.selectedIds) {
          const ids = [...state.selectedIds];
          if (ids.length === 1) {
            onSelRef.current?.(ids[0], state.doc.nodes[ids[0]] ?? null);
          } else {
            onSelRef.current?.(null, null);
          }
        }
        if (state.zoom !== prev.zoom) {
          onZoomRef.current?.(state.zoom);
        }
      });
      return () => {
        if (fontSyncTimer) clearTimeout(fontSyncTimer);
        unsub();
      };
    }, [store]);

    // Initial pass once CanvasKit is ready: a loaded scene may already use
    // italic/heavy variants the base CSS didn't seed (notably the workspace
    // surface, which seeds brand fonts only). The subscription above covers
    // every later edit.
    useEffect(() => {
      if (!ready) return;
      ensureFontVariantsLoaded(collectSceneFontVariants(store.getState().doc.nodes));
    }, [ready, store]);

    // ── Imperative handle ────────────────────────────────────────────

    useImperativeHandle(
      ref,
      () => ({
        getDocument: () => store.getState().doc,
        getStore: () => store,

        toDataURL(opts) {
          const { ck, fontMgr } = getRefs();
          if (!ck || !fontMgr) return null;

          // Capture RESOLVED pixels: thumbnails/exports bake brand tokens to
          // their current hex (the stored doc keeps them RAW).
          const state = store.getState();
          const doc = resolveDocColorsMemo(state.doc, state.brandContext);
          const scale = opts?.pixelRatio ?? 1;
          const w = Math.ceil(doc.width * scale);
          const h = Math.ceil(doc.height * scale);
          if (w <= 0 || h <= 0) return null;

          const offscreen = ck.MakeSurface(w, h);
          if (!offscreen) return null;

          const skCanvas = offscreen.getCanvas();
          skCanvas.clear(ck.Color(255, 255, 255, 1));
          skCanvas.scale(scale, scale);

          // Canvas background fills (multi-fill support)
          if (doc.fills?.length) {
            const bgRect = ck.LTRBRect(0, 0, doc.width, doc.height);
            for (const fill of doc.fills) {
              if ((fill as any).visible === false) continue;
              if (fill.type === 'IMAGE' && (fill as any).src) {
                const cached = getCachedImage((fill as any).src, Math.max(doc.width, doc.height));
                if (cached) {
                  const imgW = cached.width();
                  const imgH = cached.height();
                  const s = Math.max(doc.width / imgW, doc.height / imgH);
                  const cropW = doc.width / s;
                  const cropH = doc.height / s;
                  const cropX = (imgW - cropW) / 2;
                  const cropY = (imgH - cropH) / 2;
                  const imgPaint = new ck.Paint();
                  if (fill.opacity != null && fill.opacity < 1) imgPaint.setAlphaf(fill.opacity);
                  skCanvas.drawImageRect(
                    cached,
                    ck.LTRBRect(cropX, cropY, cropX + cropW, cropY + cropH),
                    bgRect,
                    imgPaint
                  );
                  imgPaint.delete();
                }
              } else {
                const fp = new ck.Paint();
                fp.setStyle(ck.PaintStyle.Fill);
                applyFillToPaint(ck, fp, fill, doc.width, doc.height);
                skCanvas.drawRect(bgRect, fp);
                fp.delete();
              }
            }
          }

          const ctx: RenderContext = {
            ck,
            canvas: skCanvas,
            doc,
            fontMgr,
            onImageLoaded: () => {},
            zoom: scale,
          };

          for (const rootId of doc.rootChildren) {
            const node = doc.nodes[rootId];
            if (node) renderNode(ctx, node);
          }

          offscreen.flush();
          const img = offscreen.makeImageSnapshot();
          offscreen.delete();
          if (!img) return null;

          const bytes = img.encodeToBytes();
          img.delete();
          if (!bytes) return null;
          const mimeType = opts?.mimeType ?? 'image/png';
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          return `data:${mimeType};base64,${btoa(binary)}`;
        },

        toSelectionDataURL(opts) {
          const { ck, fontMgr } = getRefs();
          if (!ck || !fontMgr) return null;

          const state = store.getState();
          // Capture RESOLVED pixels for the selection snapshot.
          const doc = resolveDocColorsMemo(state.doc, state.brandContext);
          const selectedIds = Array.from(state.selectedIds).filter(
            (id) => !doc.nodes[id]?.pluginData?.isBackground
          );
          if (selectedIds.length === 0) return null;

          const pad = opts?.padding ?? 0;
          const scale = opts?.pixelRatio ?? 2;

          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          const expandBounds = (nodeId: string) => {
            const node = doc.nodes[nodeId];
            if (!node) return;
            if ((node.type === 'GROUP' || node.type === 'FRAME') && node.children?.length) {
              for (const childId of node.children) expandBounds(childId);
              return;
            }
            const aabb = getNodeAABB(doc, nodeId);
            const strokePad = node.strokes?.length ? (node.strokeWeight ?? 1) / 2 : 0;
            minX = Math.min(minX, aabb.x - strokePad);
            minY = Math.min(minY, aabb.y - strokePad);
            maxX = Math.max(maxX, aabb.x + aabb.width + strokePad);
            maxY = Math.max(maxY, aabb.y + aabb.height + strokePad);
          };
          for (const id of selectedIds) expandBounds(id);
          if (minX >= maxX || minY >= maxY) return null;

          minX -= pad;
          minY -= pad;
          maxX += pad;
          maxY += pad;
          const w = Math.ceil((maxX - minX) * scale);
          const h = Math.ceil((maxY - minY) * scale);
          if (w <= 0 || h <= 0) return null;

          const offscreen = ck.MakeSurface(w, h);
          if (!offscreen) return null;

          const skCanvas = offscreen.getCanvas();
          skCanvas.clear(ck.Color(0, 0, 0, 0));
          skCanvas.scale(scale, scale);

          const parentOffset = getParentWorldOffset(doc, selectedIds[0]);
          skCanvas.translate(parentOffset.x - minX, parentOffset.y - minY);

          const ctx: RenderContext = {
            ck,
            canvas: skCanvas,
            doc,
            fontMgr,
            onImageLoaded: () => {},
            zoom: scale,
          };

          for (const id of selectedIds) {
            const node = doc.nodes[id];
            if (node) renderNode(ctx, node);
          }

          offscreen.flush();
          const img = offscreen.makeImageSnapshot();
          offscreen.delete();
          if (!img) return null;

          const bytes = img.encodeToBytes();
          img.delete();
          if (!bytes) return null;
          const mimeType = opts?.mimeType ?? 'image/png';
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          return `data:${mimeType};base64,${btoa(binary)}`;
        },

        toSelectionDoc() {
          const state = store.getState();
          const doc = state.doc;
          const selectedSet = new Set(
            Array.from(state.selectedIds).filter(
              (id) => doc.nodes[id] && !doc.nodes[id]?.pluginData?.isBackground
            )
          );
          if (selectedSet.size === 0) return null;

          // Drop any selected node that lives inside another selected node — its
          // ancestor already carries it, and re-rooting both would double-draw.
          const isUnderSelectedAncestor = (id: string) => {
            let pid = doc.nodes[id]?.parentId ?? null;
            while (pid) {
              if (selectedSet.has(pid)) return true;
              pid = doc.nodes[pid]?.parentId ?? null;
            }
            return false;
          };
          const roots = Array.from(selectedSet).filter((id) => !isUnderSelectedAncestor(id));
          if (roots.length === 0) return null;

          // Selection bounds in world space (mirror toSelectionDataURL).
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          const expandBounds = (nodeId: string) => {
            const node = doc.nodes[nodeId];
            if (!node) return;
            if ((node.type === 'GROUP' || node.type === 'FRAME') && node.children?.length) {
              for (const childId of node.children) expandBounds(childId);
              return;
            }
            const aabb = getNodeAABB(doc, nodeId);
            const strokePad = node.strokes?.length ? (node.strokeWeight ?? 1) / 2 : 0;
            minX = Math.min(minX, aabb.x - strokePad);
            minY = Math.min(minY, aabb.y - strokePad);
            maxX = Math.max(maxX, aabb.x + aabb.width + strokePad);
            maxY = Math.max(maxY, aabb.y + aabb.height + strokePad);
          };
          for (const id of roots) expandBounds(id);
          if (minX >= maxX || minY >= maxY) return null;

          // Collect each root subtree, deep-clone, and re-root the top-level nodes
          // at their world position minus the selection origin. Descendant coords
          // stay relative to their parent, so they're untouched.
          const nodes: Record<string, SceneNode> = {};
          const collect = (id: string) => {
            const n = doc.nodes[id];
            if (!n) return;
            nodes[id] = JSON.parse(JSON.stringify(n)) as SceneNode;
            if (n.children) for (const childId of n.children) collect(childId);
          };
          for (const id of roots) collect(id);

          for (const id of roots) {
            const off = getParentWorldOffset(doc, id);
            const src = doc.nodes[id];
            nodes[id].x = off.x + src.x - minX;
            nodes[id].y = off.y + src.y - minY;
            nodes[id].parentId = null;
          }

          return {
            version: 2,
            width: Math.max(1, Math.ceil(maxX - minX)),
            height: Math.max(1, Math.ceil(maxY - minY)),
            nodes,
            rootChildren: roots,
          };
        },

        addRect() {
          store.getState().setDrawTool('RECTANGLE');
        },

        addEllipse() {
          store.getState().setDrawTool('ELLIPSE');
        },

        addText(text = 'Text') {
          const s = store.getState();
          s.addNewNode({
            id: generateNodeId(),
            type: 'TEXT',
            parentId: null,
            x: s.doc.width / 2 - 75,
            y: s.doc.height / 2 - 12,
            width: 150,
            height: 24,
            text,
            fontSize: 16,
            fontFamily: 'Roboto',
            fontWeight: 400,
            textAlign: 'left',
            verticalAlign: 'top',
            fills: [{ type: 'SOLID', color: '#000000' }],
          });
        },

        addTextbox(text: string, opts?: { textAlign?: 'left' | 'center' | 'right' | 'justify' }) {
          const s = store.getState();
          s.addNewNode({
            id: generateNodeId(),
            type: 'TEXT',
            parentId: null,
            x: s.doc.width / 2 - 150,
            y: s.doc.height / 2 - 20,
            width: 300,
            height: 40,
            text,
            fontSize: 20,
            fontFamily: 'Roboto',
            fontWeight: 400,
            textAlign: opts?.textAlign ?? 'left',
            verticalAlign: 'top',
            fills: [{ type: 'SOLID', color: '#000000' }],
          });
        },

        addImage(url: string) {
          // Selection-first: fills a selected fillable shape (or replaces a
          // selected image); otherwise places a sized image node at center.
          applyImageToTargetOrPlace(store, url, { placeMode: 'photo' });
        },

        addProductImage(product: ProductVariantSearchResultDto) {
          const s = store.getState();
          const picked = pickedProductFromDto(product);
          // Selection inside an existing product block → rebind the whole
          // block (image, title, price, compare-at, CTA link in one step).
          const sel: string | undefined = [...s.selectedIds][0];
          const rootId = sel ? findProductBlockRoot(s.doc, sel) : null;
          if (rootId) {
            s.applyNodePatches(buildProductApplyPatches(s.doc, rootId, picked));
            return;
          }
          if (!picked.fallbackImageUrl) return;
          const size = 300;
          s.addNewNode({
            id: generateNodeId(),
            type: 'IMAGE',
            parentId: null,
            x: s.doc.width / 2 - size / 2,
            y: s.doc.height / 2 - size / 2,
            width: size,
            height: size,
            imageSrc: picked.fallbackImageUrl,
            imageAlt: picked.name,
            name: picked.name || 'product-image',
            productBinding: { productId: picked.productId, variantId: picked.variantId },
            productField: 'image',
            ...(picked.productUrl ? { linkUrl: picked.productUrl } : {}),
          });
        },

        placeImage(url: string, placement) {
          applyImageToTargetOrPlace(store, url, {
            targetNodeId: placement?.targetNodeId,
            atScene: placement?.atScene,
            placeMode: 'photo',
          });
        },

        addLine() {
          store.getState().setDrawTool('LINE');
        },

        addStar() {
          store.getState().setDrawTool('STAR');
        },

        addPolygon() {
          store.getState().setDrawTool('POLYGON');
        },

        addTrapezoid() {
          store.getState().setDrawTool('TRAPEZOID');
        },

        addSeal(opts = {}) {
          const s = store.getState();
          const doc = s.doc;
          const size = 140;
          const node: SceneNode = {
            id: generateNodeId(),
            type: 'SEAL',
            parentId: null,
            x: doc.width / 2 - size / 2,
            y: doc.height / 2 - size / 2,
            width: size,
            height: size,
            fills: [{ type: 'SOLID', color: '#DC2626' }],
            sides: opts.sides ?? 16,
            innerRadius: opts.innerRadius ?? 0.85,
            bumpStyle: opts.bumpStyle ?? 'sharp',
          };
          s.addNewNode(node);
        },

        addButton() {
          const s = store.getState();
          const groupId = generateNodeId();
          const rectId = generateNodeId();
          const textId = generateNodeId();
          // Add button rect
          const buttonRect: SceneNode = {
            id: rectId,
            type: 'RECTANGLE',
            parentId: groupId,
            x: 0,
            y: 0,
            width: 200,
            height: 48,
            fills: [{ type: 'SOLID', color: '#4F46E5' }],
            cornerRadius: 24,
          };
          // Add button text
          const buttonText: SceneNode = {
            id: textId,
            type: 'TEXT',
            parentId: groupId,
            x: 10,
            y: 14,
            width: 180,
            height: 20,
            text: 'BUTTON',
            fontSize: 14,
            fontFamily: 'Roboto',
            fontWeight: 700,
            textAlign: 'center',
            verticalAlign: 'top',
            fills: [{ type: 'SOLID', color: '#FFFFFF' }],
          };
          // Add group containing both
          const group: SceneNode = {
            id: groupId,
            type: 'GROUP',
            parentId: null,
            x: s.doc.width / 2 - 100,
            y: s.doc.height / 2 - 24,
            width: 200,
            height: 48,
            children: [rectId, textId],
            name: 'cta-button',
          };
          // Add nodes to doc manually -- group first, then children
          s.pushHistory();
          const nextNodes = {
            ...s.doc.nodes,
            [groupId]: group,
            [rectId]: buttonRect,
            [textId]: buttonText,
          };
          const nextDoc = {
            ...s.doc,
            nodes: nextNodes,
            rootChildren: [...s.doc.rootChildren, groupId],
          };
          store.setState({
            doc: nextDoc,
            selectedIds: new Set([groupId]),
            renderVersion: s.renderVersion + 1,
          });
        },

        addBadge(
          shape: 'circle' | 'rounded-rect' | 'starburst' | 'ribbon' | 'tag',
          text = '30%\nOFF'
        ) {
          const s = store.getState();
          const groupId = generateNodeId();
          const shapeId = generateNodeId();
          const textId = generateNodeId();

          // Badge dimensions and shape properties by type
          const configs: Record<
            string,
            {
              w: number;
              h: number;
              nodeType: SceneNode['type'];
              extra: Partial<SceneNode>;
              textY: number;
            }
          > = {
            circle: {
              w: 80,
              h: 80,
              nodeType: 'ELLIPSE',
              extra: {},
              textY: 20,
            },
            'rounded-rect': {
              w: 120,
              h: 40,
              nodeType: 'RECTANGLE',
              extra: { cornerRadius: 20 },
              textY: 10,
            },
            starburst: {
              w: 90,
              h: 90,
              nodeType: 'STAR',
              extra: { sides: 12, innerRadius: 0.75 },
              textY: 26,
            },
            ribbon: {
              w: 160,
              h: 36,
              nodeType: 'PATH',
              extra: {
                pathData: 'M0,0 L140,0 L160,18 L140,36 L0,36 Z',
              },
              textY: 8,
            },
            tag: {
              w: 100,
              h: 44,
              nodeType: 'RECTANGLE',
              extra: { cornerRadius: 6 },
              textY: 8,
            },
          };

          const cfg = configs[shape] || configs['circle'];
          const bgColor = '#E53E3E';
          const textColor = '#FFFFFF';

          const badgeShape: SceneNode = {
            id: shapeId,
            type: cfg.nodeType,
            parentId: groupId,
            x: 0,
            y: 0,
            width: cfg.w,
            height: cfg.h,
            fills: [{ type: 'SOLID', color: bgColor }],
            name: 'badge-bg',
            ...cfg.extra,
          };

          const badgeText: SceneNode = {
            id: textId,
            type: 'TEXT',
            parentId: groupId,
            x: 4,
            y: cfg.textY,
            width: cfg.w - 8,
            height: cfg.h - cfg.textY * 2,
            text,
            fontSize: shape === 'ribbon' || shape === 'rounded-rect' ? 13 : 14,
            fontFamily: 'Roboto',
            fontWeight: 800,
            textAlign: 'center',
            verticalAlign: 'middle',
            fills: [{ type: 'SOLID', color: textColor }],
            letterSpacing: 0.5,
            name: 'badge-text',
          };

          const group: SceneNode = {
            id: groupId,
            type: 'GROUP',
            parentId: null,
            x: s.doc.width - cfg.w - 20,
            y: 20,
            width: cfg.w,
            height: cfg.h,
            children: [shapeId, textId],
            name: 'badge',
            slotRole: 'badge',
          };

          s.pushHistory();
          const nextNodes = {
            ...s.doc.nodes,
            [groupId]: group,
            [shapeId]: badgeShape,
            [textId]: badgeText,
          };
          const nextDoc = {
            ...s.doc,
            nodes: nextNodes,
            rootChildren: [...s.doc.rootChildren, groupId],
          };
          store.setState({
            doc: nextDoc,
            selectedIds: new Set([groupId]),
            renderVersion: s.renderVersion + 1,
          });
        },

        setDrawTool: (tool: DrawToolType | null) => store.getState().setDrawTool(tool),

        deleteSelected: () => store.getState().removeSelected(),
        undo: () => store.getState().undo(),
        redo: () => store.getState().redo(),

        get canUndo() {
          return store.getState().undoStack.length > 0;
        },
        get canRedo() {
          return store.getState().redoStack.length > 0;
        },

        bringForward: () => store.getState().moveZOrder('forward'),
        sendBackward: () => store.getState().moveZOrder('backward'),
        bringToFront: () => store.getState().moveZOrder('toFront'),
        sendToBack: () => store.getState().moveZOrder('toBack'),
        groupSelected: () => store.getState().groupSelected(),
        ungroupSelected: () => store.getState().ungroupSelected(),

        textToOutlines(nodeId: string) {
          const { ck, fontMgr } = getRefs();
          if (!ck || !fontMgr) return;
          store.getState().textToOutlines(nodeId, ck, fontMgr);
        },

        booleanOp(op: 'union' | 'subtract' | 'intersect' | 'xor') {
          const { ck } = getRefs();
          if (!ck) return;
          store.getState().booleanOp(op, ck);
        },

        importSvg(svgString: string) {
          store.getState().importSvg(svgString);
        },

        addLinkZone() {
          store.getState().addLinkZone();
        },

        setLinkZoneLayout(layoutId: string) {
          store.getState().setLinkZoneLayout(layoutId);
        },

        loadDocument(doc: SceneDocument) {
          store.getState().setDocument(doc);
        },

        addTemplate(doc: SceneDocument, name: string) {
          store.getState().addTemplateAsGroup(doc, name);
        },

        resizeCanvas(width: number, height: number) {
          store.getState().updateDocumentSize(width, height);
        },

        resetHistory() {
          store.setState({ undoStack: [], redoStack: [] });
        },

        toJSON() {
          return store.getState().doc;
        },
      }),
      [store, getRefs]
    );

    // ── Custom event listeners for text-to-outlines and boolean ops ──

    useEffect(() => {
      const handleTextToOutlines = (e: Event) => {
        const detail = (e as CustomEvent).detail;
        if (detail?.nodeId) {
          const { ck, fontMgr } = getRefs();
          if (ck && fontMgr) {
            store.getState().textToOutlines(detail.nodeId, ck, fontMgr);
          }
        }
      };
      const handleBooleanOp = (e: Event) => {
        const detail = (e as CustomEvent).detail;
        if (detail?.op) {
          const { ck } = getRefs();
          if (ck) {
            store.getState().booleanOp(detail.op, ck);
          }
        }
      };
      window.addEventListener('nexmail:textToOutlines', handleTextToOutlines);
      window.addEventListener('nexmail:booleanOp', handleBooleanOp);
      return () => {
        window.removeEventListener('nexmail:textToOutlines', handleTextToOutlines);
        window.removeEventListener('nexmail:booleanOp', handleBooleanOp);
      };
    }, [store, getRefs]);

    // ── Render ───────────────────────────────────────────────────────

    if (error) {
      return (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    const content = (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#e8e8e8',
        }}
        onDragOver={(e) => {
          if (isCanvasAssetDrag(e) || (onOsFilesDropped && isOsFileDrag(e))) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }
        }}
        onDrop={(e) => {
          if (handleCanvasAssetDrop(e, store, canvasRef.current)) return;
          // OS file drop (photo dragged in from the desktop): hand the files
          // to the owner to upload, with the drop placement so the result
          // lands where the user dropped it.
          if (!onOsFilesDropped || !canvasRef.current) return;
          const files = readOsImageFiles(e);
          if (!files.length) return;
          e.preventDefault();
          onOsFilesDropped(files, resolveDropPlacement(e, store, canvasRef.current));
        }}
      >
        {!ready && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#e8e8e8',
              zIndex: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('canvasKitEditor.loading', 'Loading CanvasKit...')}
            </Typography>
          </Box>
        )}
        <canvas
          ref={canvasRef}
          onPointerDown={events.handlePointerDown}
          onPointerMove={events.handlePointerMove}
          onPointerUp={events.handlePointerUp}
          onDoubleClick={events.handleDoubleClick}
          onContextMenu={(e) => {
            e.preventDefault();
            if (onContextMenuProp) {
              const s = store.getState();
              const ids = [...s.selectedIds];
              const hasGroup = ids.length >= 2;
              const canUngroup =
                ids.length === 1 &&
                (s.doc.nodes[ids[0]]?.type === 'GROUP' || s.doc.nodes[ids[0]]?.type === 'FRAME');
              onContextMenuProp({
                mouseX: e.clientX,
                mouseY: e.clientY,
                selectedIds: ids,
                hasGroup,
                canUngroup,
              });
            }
          }}
          style={{ display: 'block', width: '100%', height: '100%', touchAction: 'none' }}
        />
        <InlineTextOverlay store={store} />
        <SelectionToolbar store={store} />
        {ready && <CanvasShortcutsHelp />}
        {children}
      </Box>
    );

    // Skip wrapping with provider if one already exists from CanvasKitEditorProvider
    if (existingStore) return content;
    return <EditorStoreContext.Provider value={store}>{content}</EditorStoreContext.Provider>;
  }
);
